// libs
import React, { useCallback, useMemo } from 'react';

import { CheckmarkOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Avatar, BaseMenuItem, Checkbox, Stack, Tooltip, Typography } from '@lyearn/ui';
import { UserStatusEnum } from '@/types/schema';

import { FilterItemProps } from './types';

const defaultItemRenderer = ({
  isSelected,
  option,
}: {
  isSelected?: boolean;
  option: FilterItemProps['option'];
}) => (
  <>
    {option.Icon ? (
      <Stack
        alignItems="center"
        className={cn('me-2 ms-12', isSelected ? 'text-icon-active' : 'text-icon-secondary')}
        justifyContent="center">
        <option.Icon className="h-20 w-20" />
      </Stack>
    ) : option.src !== undefined ? (
      <Avatar className="me-2 ms-12" size={22} src={option.src} />
    ) : null}
    <Typography
      className="xs:body-short-01 truncate ms-12"
      color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
      variant="body-short-02">
      {option.label}
    </Typography>
  </>
);

// check where children is used @iamsaumya
function FilterItemDesktop(props: FilterItemProps) {
  const {
    option,
    isSelected: _isSelected,
    onClick,
    itemRenderer = defaultItemRenderer,
    isSingleSelect,
    className,
    getTooltipProps,
  } = props;

  const hasConfigToRender = !!option.configToRenderFilterBody;
  const isSelected = !hasConfigToRender && _isSelected;

  const _onClick = useCallback(() => {
    onClick?.(option);
  }, [onClick, option]);

  const renderSelectedAction = () => {
    if (!isSingleSelect) {
      return (
        <Checkbox
          checked={isSelected}
          className="rounded-4 bg-bg-primary"
          disabled={option.disabled}
        />
      );
    }

    if (isSelected) {
      return <CheckmarkOutline className="h-16 w-16 flex-shrink-0" />;
    }

    return null;
  };

  const renderedItem = useMemo(() => {
    return itemRenderer({
      isSelected,
      option,
    });
  }, [isSelected, itemRenderer, option]);

  const renderMenuItem = () => (
    <BaseMenuItem
      className={cn(
        'my-2 flex w-full items-center justify-between rounded-8 py-8 ps-0 pe-12 space-s-12',
        { 'pointer-events-none opacity-50': option.disabled },
        className,
      )}
      tabIndex={option.disabled ? -1 : 0}
      onClick={option.disabled ? undefined : _onClick}>
      <Stack alignItems="center" className="w-[86%]">
        {renderedItem}
      </Stack>
      {renderSelectedAction()}
    </BaseMenuItem>
  );

  const isUserArchived = option?.status === UserStatusEnum.Archived;
  const tooltipPropsValue = getTooltipProps?.({ option });

  if (tooltipPropsValue && !isUserArchived) {
    return (
      <Tooltip enterDelay={400} {...tooltipPropsValue}>
        {renderMenuItem()}
      </Tooltip>
    );
  }

  return renderMenuItem();
}

export default React.memo(FilterItemDesktop);
